import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Attendees, Gallery } from '../components/EventSocial';
import { EventComms } from '../components/EventComms';
import { RsvpPanel } from '../components/RsvpPanel';
import { TestEventBadge } from '../components/TestEventBadge';
import { TestRoster } from '../components/TestRoster';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { can } from '@gwc/shared';
import { rsvpApi } from '../api/rsvp';
import { tastingsApi, type UpdateEventInput } from '../api/tastings';
import type { EventRow } from '../api/models';
import { useAuth } from '../auth/AuthProvider';
import { eventDate, PHASE_LABEL } from '../lib/format';
import { ApiError } from '../lib/api';

export function EventDetail() {
  const { id = '' } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const canManage = user ? can(user.roles, 'tasting:create') : false;
  const canRun = user ? can(user.roles, 'tasting:run') : false;
  const canComms = user ? can(user.roles, 'comms:event') : false;

  const { data, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: () => tastingsApi.get(id),
  });
  // Same query (and cache entry) as the RSVP panel's.
  const { data: summary } = useQuery({
    queryKey: ['rsvp', id],
    queryFn: () => rsvpApi.summary(id),
  });

  if (isLoading || !data) return <p className="text-cream-300/60">Loading…</p>;
  const { event } = data;
  const editable =
    canManage &&
    event.canManage === true &&
    (event.status === 'upcoming' || event.status === 'live');

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/tastings" className="text-xs text-cream-300/60 hover:text-gold-200">
        ← All tastings
      </Link>
      <div className="mt-2 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl text-cream-50">{event.title}</h1>
          <TestEventBadge isTest={event.isTest} />
        </div>
        <p className="text-sm text-cream-300/60">
          {eventDate(event)} ·{' '}
          <span className="text-gold-300">{PHASE_LABEL[event.phase] ?? event.phase}</span>
        </p>
        {event.isTest && (
          <p className="mt-2 rounded border border-warning/30 bg-warning/5 px-3 py-2 text-xs text-cream-200">
            This is a practice tasting. Everything here works exactly as it does on the night, but
            it is left out of the archive, the club statistics and every export — and it only
            messages the testers added to it.
          </p>
        )}
      </div>

      {editable ? (
        <EventEditForm
          event={event}
          onSaved={() => void qc.invalidateQueries({ queryKey: ['event', id] })}
        />
      ) : (
        <EventFacts event={event} summary={summary} />
      )}

      <RsvpPanel eventId={id} />

      <div className="flex gap-2">
        <Link to={`/live/${id}`} className="btn-ghost">
          Live view
        </Link>
        {canManage && event.canManage === true && (
          <Link to={`/tastings/${id}/setup`} className="btn-ghost">
            Setup
          </Link>
        )}
        {canRun && event.canManage === true && (
          <Link to={`/tastings/${id}/dashboard`} className="btn-ghost">
            Dashboard
          </Link>
        )}
        {canComms && event.canManage === true && event.phase === 'final' && (
          <Link to={`/tastings/${id}/wrapup`} className="btn-ghost">
            Wrap-up email
          </Link>
        )}
      </div>

      <section className="card mt-6 space-y-4 p-6">
        <h2 className="font-display text-xl text-cream-50">Who's coming</h2>
        <Attendees eventId={id} />
      </section>

      <section className="card mt-6 space-y-4 p-6">
        <h2 className="font-display text-xl text-cream-50">Photos from the night</h2>
        <Gallery eventId={id} />
      </section>

      {/* The roster sits above the composer: who a rehearsal can reach is the
          thing to settle before writing anything to send them. */}
      {canManage && event.canManage === true && event.isTest && <TestRoster eventId={id} />}

      {canComms && event.canManage === true && <EventComms event={event} />}

      {canRun && event.canManage === true && <AttendeeList id={id} />}
    </div>
  );
}

function eventToForm(event: EventRow): UpdateEventInput {
  return {
    title: event.title,
    theme: event.theme ?? '',
    regionLocation: event.regionLocation ?? '',
    venue: event.venueName ?? '',
    eventDate: event.eventDate ?? '',
    eventYear: event.eventYear,
    eventMonth: event.eventMonth ?? null,
    datePrecision: event.datePrecision,
    isScored: event.isScored,
    dressCode: event.dressCode ?? '',
    price: event.price != null && event.price !== '' ? Number(event.price) : null,
    capacity: event.capacity ?? null,
    notes: event.notes ?? '',
  };
}

function EventEditForm({ event, onSaved }: { event: EventRow; onSaved: () => void }) {
  const [form, setForm] = useState<UpdateEventInput>(() => eventToForm(event));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      tastingsApi.update(event.id, {
        title: form.title,
        theme: form.theme || null,
        regionLocation: form.regionLocation || null,
        venue: form.venue || null,
        eventDate: form.eventDate || null,
        eventYear: form.eventYear,
        eventMonth: form.eventMonth,
        datePrecision: form.datePrecision,
        isScored: form.isScored,
        dressCode: form.dressCode || null,
        price: form.price,
        capacity: form.capacity,
        notes: form.notes || null,
      }),
    onSuccess: () => {
      setSaved(true);
      setError(null);
      onSaved();
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save'),
  });

  const set = (patch: Partial<UpdateEventInput>) => setForm((f) => ({ ...f, ...patch }));

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <form onSubmit={submit} className="card mb-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm uppercase tracking-[0.15em] text-gold-400">Tasting details</h2>
        {saved && <span className="text-xs text-gold-200">Saved ✓</span>}
      </div>
      {error && <p className="text-sm text-wine-300">{error}</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Labeled label="Title">
          <input
            className="field"
            value={form.title ?? ''}
            onChange={(e) => set({ title: e.target.value })}
            required
          />
        </Labeled>
        <Labeled label="Theme">
          <input
            className="field"
            value={form.theme ?? ''}
            onChange={(e) => set({ theme: e.target.value })}
            placeholder="Napa Cabernet"
          />
        </Labeled>
        <Labeled label="Region / location">
          <input
            className="field"
            value={form.regionLocation ?? ''}
            onChange={(e) => set({ regionLocation: e.target.value })}
            placeholder="Monterey Peninsula"
          />
        </Labeled>
        <Labeled label="Venue">
          <input
            className="field"
            value={form.venue ?? ''}
            onChange={(e) => set({ venue: e.target.value })}
            placeholder="L'Escargot"
          />
        </Labeled>
        <Labeled label="Date">
          <input
            type="date"
            className="field"
            value={form.eventDate ?? ''}
            onChange={(e) => {
              const d = e.target.value;
              set({
                eventDate: d,
                eventYear: Number(d.slice(0, 4)),
                eventMonth: Number(d.slice(5, 7)),
                datePrecision: 'day',
              });
            }}
            required
          />
        </Labeled>
        <Labeled label="Dress code">
          <input
            className="field"
            value={form.dressCode ?? ''}
            onChange={(e) => set({ dressCode: e.target.value })}
            placeholder="Dark suit"
          />
        </Labeled>
        <Labeled label="Price">
          <input
            type="number"
            min={0}
            step={0.01}
            className="field"
            value={form.price ?? ''}
            onChange={(e) => set({ price: e.target.value ? Number(e.target.value) : null })}
            placeholder="No charge"
          />
        </Labeled>
        <Labeled label="Capacity">
          <input
            type="number"
            min={1}
            className="field"
            value={form.capacity ?? ''}
            onChange={(e) => set({ capacity: e.target.value ? Number(e.target.value) : null })}
            placeholder="No limit"
          />
        </Labeled>
      </div>
      <Labeled label="Notes">
        <textarea
          className="field min-h-[70px]"
          value={form.notes ?? ''}
          onChange={(e) => set({ notes: e.target.value })}
        />
      </Labeled>
      <label className="flex items-center gap-2 text-sm text-cream-200">
        <input
          type="checkbox"
          checked={form.isScored ?? true}
          onChange={(e) => set({ isScored: e.target.checked })}
          className="rounded border-ink-700"
        />
        Scored tasting
      </label>
      <button type="submit" className="btn-primary" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  );
}

function EventFacts({
  event,
  summary,
}: {
  event: EventRow;
  summary?: { seatsUsed: number; waitlistCount: number } | null;
}) {
  return (
    <div className="card mb-6 grid gap-2 text-sm">
      <Fact label="Theme" value={event.theme} />
      <Fact label="Region" value={event.regionLocation} />
      <Fact label="Venue" value={event.venueName} />
      <Fact label="Dress" value={event.dressCode} />
      <Fact label="Price" value={event.price ? `$${event.price}` : null} />
      {event.capacity != null && summary && (
        <Fact
          label="Seats"
          value={`${summary.seatsUsed} of ${event.capacity} taken${
            summary.waitlistCount ? ` · ${summary.waitlistCount} waitlisted` : ''
          }`}
        />
      )}
      <Fact label="Notes" value={event.notes} />
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">{label}</span>
      {children}
    </label>
  );
}

function Fact({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-3">
      <span className="w-16 shrink-0 text-xs uppercase tracking-wide text-gold-400">{label}</span>
      <span className="text-cream-100">{value}</span>
    </div>
  );
}

function AttendeeList({ id }: { id: string }) {
  const { data: registrations = [] } = useQuery({
    queryKey: ['registrations', id],
    queryFn: () => tastingsApi.registrations(id),
  });
  const attending = registrations.filter((r) => r.status !== 'cancelled');
  if (attending.length === 0) return null;
  return (
    <div className="card mt-6">
      <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">
        Attendees ({attending.length})
      </h2>
      <div className="space-y-1 text-sm">
        {attending.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between border-b border-ink-800/60 py-1.5 last:border-0"
          >
            <span className="text-cream-100">
              {r.firstName} {r.lastName}
              {r.guestsCount > 0 && <span className="text-cream-300/50"> +{r.guestsCount}</span>}
            </span>
            <span
              className={`text-xs ${r.status === 'waitlist' ? 'text-gold-300' : 'text-cream-300/50'}`}
            >
              {r.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
